import { Link } from 'react-router-dom';
import { Box, Flex } from '@chakra-ui/react';
import { Avatar, Checkbox, Text } from '@kula-ai/components';
import { Stars02 } from '@kula-ai/icons-core';
import { toInternalPath } from '../../../helpers';
import { useAgentForgeStore } from '../../../stores';
import type { CandidateSummary } from '../../../types';
import { MatchPill } from '../primitives';
import { CandidateTags } from './CandidateTags';
import { aspectLabel, buildSubheadline, splitReason } from './candidateCopy';
import { SPARKLE_GRADIENT_CSS, SPARKLE_GRADIENT_ID } from './SparkleGradient';

export const CandidateCard = ({
  candidate,
  checked,
  onToggle,
}: {
  candidate: CandidateSummary;
  checked: boolean;
  onToggle: () => void;
}) => {
  const openFloatingBridge = useAgentForgeStore((s) => s.openFloatingBridge);
  const subheadline = buildSubheadline(candidate);
  return (
    <Box
      data-test-id={`agentforge-candidate-card-${candidate.id}`}
      border="1px solid"
      borderColor={checked ? 'primary-300' : 'secondary-100'}
      borderRadius="12px"
      bg="base-white"
      p="14px"
      transition="border-color 0.15s ease"
      _hover={{ borderColor: checked ? 'primary-300' : 'secondary-200' }}
    >
      {/* Checkbox + avatar form a fixed gutter; the name, tags, and match
          reason share one indented content column so they line up. */}
      <Flex align="flex-start" gap="10px">
        <Checkbox
          size="sm"
          checked={checked}
          onChange={onToggle}
          aria-label={`Select ${candidate.name}`}
          mt="2px"
        />
        <Avatar
          src={candidate.avatarUrl}
          name={candidate.name}
          shape="square"
          size={40}
        />
        <Box flex="1" minW="0">
          <Flex align="flex-start" justify="space-between" gap="10px">
            <Flex direction="column" minW="0">
              {/* Name links to the backend-supplied profile href, else plain text. */}
              {candidate.profileHref ? (
                <Box
                  as={Link}
                  to={toInternalPath(candidate.profileHref)}
                  minW="0"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    openFloatingBridge(toInternalPath(candidate.profileHref!));
                  }}
                  _hover={{ textDecoration: 'underline' }}
                  data-test-id={`agentforge-candidate-profile-link-${candidate.id}`}
                >
                  <Text
                    variant="h4"
                    as="span"
                    truncate
                    className="text-secondary-700"
                  >
                    {candidate.name}
                  </Text>
                </Box>
              ) : (
                <Text
                  variant="h4"
                  as="span"
                  truncate
                  className="text-secondary-700"
                >
                  {candidate.name}
                </Text>
              )}
              {subheadline && (
                <Text variant="caption" className="text-secondary-600 truncate">
                  {subheadline}
                </Text>
              )}
            </Flex>
            {candidate.matchScore && (
              <MatchPill
                sparkle
                // match_score is a 0-1 fraction; MatchPill wants 0-100.
                pct={Math.round(candidate.matchScore.value * 100)}
              />
            )}
          </Flex>

          <CandidateTags candidate={candidate} />

          {/* AI "why this matched" line. */}
          {candidate.matchReason && (
            <Flex
              align="flex-start"
              gap="8px"
              mt="10px"
              p="10px"
              borderRadius="8px"
              bg="#F0E7FE45"
            >
              <Box
                as="span"
                display="inline-flex"
                mt="2px"
                sx={{
                  '& path': {
                    fill: `url(#${SPARKLE_GRADIENT_ID})`,
                    stroke: `url(#${SPARKLE_GRADIENT_ID})`,
                  },
                }}
              >
                <Stars02 size={14} />
              </Box>
              {(() => {
                const { lead, rest } = splitReason(candidate.matchReason);
                return (
                  <Box as="span" fontSize="12px" lineHeight="1.45">
                    <Box
                      as="span"
                      fontWeight="500"
                      bgGradient={SPARKLE_GRADIENT_CSS}
                      bgClip="text"
                      color="transparent"
                    >
                      {lead}
                    </Box>
                    {rest && (
                      <Box as="span" color="secondary-700">
                        {' '}
                        {rest}
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </Flex>
          )}

          {/* Matched-aspect evidence snippets (note / scorecard / transcript). */}
          {candidate.evidence && candidate.evidence.length > 0 && (
            <Flex direction="column" gap="6px" mt="10px">
              {candidate.evidence.map((e, i) => (
                <Flex
                  key={`${e.aspect}-${i}`}
                  align="flex-start"
                  gap="8px"
                  p="10px"
                  borderRadius="8px"
                  bg="secondary-50"
                  border="1px solid"
                  borderColor="border-color"
                >
                  <Box
                    as="span"
                    flexShrink={0}
                    px="6px"
                    py="2px"
                    borderRadius="6px"
                    bg="base-white"
                    border="1px solid"
                    borderColor="border-color"
                    color="secondary-700"
                    fontSize="12px"
                    fontWeight="600"
                    textTransform="uppercase"
                    letterSpacing="0.03em"
                  >
                    {aspectLabel(e.aspect)}
                    {typeof e.rating === 'number' ? ` ${e.rating}/4` : ''}
                  </Box>
                  <Box
                    as="span"
                    fontSize="12px"
                    lineHeight="1.45"
                    color="secondary-800"
                  >
                    {e.snippet}
                  </Box>
                </Flex>
              ))}
            </Flex>
          )}
        </Box>
      </Flex>
    </Box>
  );
};
